import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Loader2 } from "lucide-react";
import { getIcon } from "../data/site";
import { useContent } from "../context/ContentContext";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { content, loading } = useContent();
  const service = content.services.find((s) => s.slug === slug);
  if (loading && !service) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (!service) return <Navigate to="/services" replace />;

  const Icon = getIcon(service.iconName);

  return (
    <div data-testid="service-detail-page">
      <Seo title={service.title} description={service.overview.slice(0, 155)} />

      {/* banner */}
      <section className="relative min-h-[60vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={service.image} alt={service.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative container-px mx-auto max-w-7xl pb-14 pt-32">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-white/15 text-white backdrop-blur"><Icon className="h-6 w-6" strokeWidth={1.6} /></span>
            <h1 className="mt-5 text-4xl sm:text-5xl font-bold tracking-tight text-white text-balance max-w-3xl">{service.title}</h1>
            <p className="mt-4 max-w-2xl text-lg text-slate-200">{service.short}</p>
          </motion.div>
        </div>
      </section>

      {/* overview + capabilities */}
      <section className="container-px mx-auto max-w-7xl py-20 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-3">
          <Reveal className="lg:col-span-2">
            <p className="overline">Overview</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight">What we deliver</h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">{service.overview}</p>
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {service.capabilities.map((c) => (
                <div key={c} className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-secondary/15 text-secondary"><Check className="h-4 w-4" /></span>
                  <span className="text-sm font-medium">{c}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-border bg-primary p-8 text-white">
              <h3 className="font-heading text-xl font-semibold">Get started</h3>
              <p className="mt-2 text-sm text-slate-200">Request a tailored quote for {service.title.toLowerCase()} today.</p>
              <Link to="/contact" className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-accent px-6 py-3 text-sm font-semibold text-[#06281A] transition-transform hover:scale-105" data-testid="service-detail-quote-btn">
                Request a Quote <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* benefits */}
      <section className="bg-surface py-20 lg:py-24">
        <div className="container-px mx-auto max-w-7xl">
          <Reveal><p className="overline">Benefits</p><h2 className="mt-3 text-3xl font-semibold tracking-tight">Why it matters</h2></Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {service.benefits.map((b, i) => {
              const BIcon = getIcon(b.iconName);
              return (
                <Reveal key={b.title} delay={i * 0.08}>
                  <div className="h-full rounded-2xl border border-border bg-card p-7">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><BIcon className="h-6 w-6" strokeWidth={1.6} /></span>
                    <h3 className="mt-5 font-heading text-lg font-semibold">{b.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{b.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* process */}
      <section className="container-px mx-auto max-w-7xl py-20 lg:py-24">
        <Reveal><p className="overline">Our Process</p><h2 className="mt-3 text-3xl font-semibold tracking-tight">How we work</h2></Reveal>
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {service.process.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <div className="relative h-full rounded-2xl border border-border bg-card p-7">
                <span className="font-heading text-4xl font-bold text-accent/40">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-2 font-heading text-lg font-semibold">{p.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* faqs */}
      <section className="bg-surface py-20 lg:py-24">
        <div className="container-px mx-auto max-w-3xl">
          <Reveal><p className="overline text-center">FAQ</p><h2 className="mt-3 text-center text-3xl font-semibold tracking-tight">Frequently asked questions</h2></Reveal>
          <Reveal delay={0.1} className="mt-10">
            <Accordion type="single" collapsible className="w-full" data-testid="service-faq-accordion">
              {service.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="rounded-xl border border-border bg-card mb-3 px-5">
                  <AccordionTrigger className="text-left font-heading font-medium">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
